import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { getAppUser, type AppUser } from '../services/appUser.service';
import { getOrgMember } from '../services/org.service';
import { OrgContext } from './org-context';
import { PermissionsContext } from './permissions-context';
import { useAuth } from './AuthContext';

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user, isLoading } = useAuth();
  // Optional — null when PermissionsProvider is mounted outside OrgProvider (e.g. /org/new).
  // When PermissionsProvider is mounted inside OrgProvider (after route restructure in #182),
  // this will return the active org and permissions will be org-scoped.
  const orgContext = useContext(OrgContext);
  const orgId = orgContext?.orgId ?? null;

  // When a parent PermissionsProvider already loaded appUser (e.g. the global provider
  // in App.tsx), inherit it instead of issuing a duplicate Firestore read. The inner
  // provider only mounts after OrgProvider confirms the parent is fully loaded, so
  // parentCtx.appUser is guaranteed to be set at this point.
  const parentCtx = useContext(PermissionsContext);
  const mountedWithParent = useRef(parentCtx !== null);
  const inheritedAppUser = useRef(parentCtx?.appUser ?? null);

  const [appUser, setAppUser] = useState<AppUser | null>(inheritedAppUser.current);
  const [appUserLoaded, setAppUserLoaded] = useState(mountedWithParent.current);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Load global AppUser profile — skipped for inner provider since parent already has it.
  useEffect(() => {
    if (mountedWithParent.current) return;
    if (isLoading) return;

    if (!user) {
      setAppUser(null);
      setAppUserLoaded(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await getAppUser(user.uid);
        if (!cancelled) {
          if (!data) {
            setAppUserLoaded(true);
            return;
          }
          // TODO: Schema version check temporarily disabled for debugging
          // if (schemaVersion < EXPECTED_SCHEMA_VERSION) {
          //   await firebaseSignOut();
          //   setAppUserLoaded(true);
          //   return;
          // }
          setAppUser(data);
          setAppUserLoaded(true);
        }
      } catch {
        if (!cancelled) setAppUserLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user, isLoading]);

  // Load org-scoped permissions from orgs/{orgId}/members/{uid}.
  // Re-runs whenever the active org changes (orgId from OrgContext).
  useEffect(() => {
    if (!orgId || !user || isLoading) {
      setPermissions([]);
      setPermissionsLoaded(true);
      return;
    }

    setPermissionsLoaded(false);
    let cancelled = false;

    (async () => {
      try {
        const member = await getOrgMember(orgId, user.uid);
        if (!cancelled) {
          setPermissions(member?.permissions ?? []);
          setPermissionsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPermissions([]);
          setPermissionsLoaded(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [orgId, user, isLoading]);

  const isLoadingPermissions = !appUserLoaded || !permissionsLoaded;

  const hasPermission = useCallback(
    (perm: string): boolean => {
      if (!appUser || appUser.isDisabled) return false;
      return permissions.includes(perm);
    },
    [appUser, permissions],
  );

  const refetchAppUser = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getAppUser(user.uid);
      if (data) {
        setAppUser(data);
      }
    } catch (error) {
      console.error('Failed to refetch appUser:', error);
    }
  }, [user]);

  return (
    <PermissionsContext.Provider value={{ appUser, isLoadingPermissions, hasPermission, refetchAppUser }}>
      {children}
    </PermissionsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
